import { Link } from 'react-router-dom'
import type { ReactNode } from 'react'

import { LogoMark } from '@/components/layout/Logo'
import { mutedTextClass, surfaceClass } from '@/components/layout/ui'
import { cn } from '@/lib/cn'

/**
 * The centred card every signed-out screen shares.
 *
 * One card on paper, the mark above it and the one secondary action below it,
 * so the page is recognisably Taskly before anything has loaded and there is
 * never a second thing competing for the click. The measure is narrow on
 * purpose: a sign-in form that spans a desktop window reads as a settings
 * page, not as a door.
 *
 * Heading and description are centred; everything inside `children` is not.
 * Centred labels and inputs are harder to scan, and the form is the part
 * people are actually working through.
 */
export function AuthShell({
  title,
  description,
  children,
  footer,
}: {
  title: string
  description?: ReactNode
  children: ReactNode
  footer?: ReactNode
}) {
  return (
    <main className="flex min-h-dvh flex-col items-center justify-center px-6 py-12">
      <div className="w-full max-w-[25rem]">
        <Link
          to="/login"
          className="mx-auto mb-7 flex w-fit items-center gap-2.5 rounded-lg"
          aria-label="Taskly"
        >
          <LogoMark className="size-8" />
          <span className="text-lg font-semibold tracking-tight">Taskly</span>
        </Link>

        <div className={cn(surfaceClass, 'px-7 py-8 shadow-sm shadow-ink-950/5')}>
          <div className="text-center">
            <h1 className="text-xl font-semibold tracking-tight text-balance">{title}</h1>
            {description ? (
              <p className={cn('mx-auto mt-2 max-w-[32ch] text-sm text-pretty', mutedTextClass)}>
                {description}
              </p>
            ) : null}
          </div>

          <div className="mt-7">{children}</div>
        </div>

        {footer ? (
          <div className={cn('mt-6 text-center text-sm', mutedTextClass)}>{footer}</div>
        ) : null}
      </div>
    </main>
  )
}
